import { createRoot } from "solid-js";
import { createStore } from "solid-js/store";
import { setKnownPrincipal, setKnownSignedIn, clearSessionExpired } from "@/auth/session";
import { sessionRemove } from "@/shared/safeStorage";
import {
  api,
  SALES_TOKEN_KEY,
  type SessionContext,
  type ResolvedCustomer,
  type SessionPrincipal,
  type SessionCapabilities,
} from "@/api/client";

/**
 * Resolves and holds the per-browser session context — the single capability model that drives
 * landing page, project scope, save, ordering and net-price visibility (mirrors the backend
 * SessionContextResolver). A deep-link <c>?token=</c> (sales rep via D365FO) takes precedence:
 * it is verified once via resolve-token, then persisted for the browser session and resent on
 * every request as the <c>X-Sales-Token</c> header (see api/client). Defaults are the most
 * restrictive (Basic) until the context loads.
 */
/** The signed-in DB user (name/email + live dealer/company), for the profile UI. */
export interface SessionUser {
  name: string | null;
  email: string | null;
  dealerId: string | null;
  companyName: string | null;
}

interface SessionState {
  context: SessionContext | null;
  customer: ResolvedCustomer | null; // the ERP customer a token resolved to (Internal)
  user: SessionUser | null; // the signed-in DB user (authenticated path only) — single source for the profile
  hasToken: boolean;
  loaded: boolean;
  loading: boolean;
  /** The context request itself failed (network/server) — NOT "resolved to Basic". Consumers must not
   *  treat a failed load as a Basic principal (e.g. HomePage's redirect); offer a retry instead. */
  failed: boolean;
  // Dev-only: overrides the resolved principal so UI gating can be previewed as each actor.
  // Does NOT change server behavior (the backend still enforces by the real principal).
  devPrincipal: SessionPrincipal | null;
}

/**
 * The principal preview outlives a page load.
 *
 * <p>It is a module signal otherwise, and every full navigation — a hard link, a refresh, the admin
 * page's own back button — dropped it, so switching to "dealer" and then opening the configurator
 * showed the real principal again and the switcher looked like it did nothing. sessionStorage is the
 * right scope: it is a preview for this tab, cleared when the tab closes.</p>
 *
 * <p>Restored only once the session has loaded and holds Admin (see {@link previewFor}). The tab outlives
 * the person in it: whoever signs in next, or the same person after their Admin is taken away, was handed
 * the preview too, and one left on "internal" showed them the rep's products.</p>
 */
const DEV_PRINCIPAL_KEY = "lob-dev-principal";

function readDevPrincipal(): SessionPrincipal | null {
  try {
    const v = sessionStorage.getItem(DEV_PRINCIPAL_KEY);
    return v === "internal" || v === "dealer" || v === "basic" ? v : null;
  } catch {
    return null;
  }
}

/** The stored preview, for an administrator's session; anyone else's discards it. */
function previewFor(context: SessionContext): SessionPrincipal | null {
  if (context.permissions?.includes("admin")) return readDevPrincipal();
  sessionRemove(DEV_PRINCIPAL_KEY);
  return null;
}

function createSessionStore() {
  const [state, setState] = createStore<SessionState>({
    context: null,
    customer: null,
    user: null,
    hasToken: false,
    loaded: false,
    loading: false,
    failed: false,
    devPrincipal: null,
  });

  // The in-flight load, so concurrent callers await the SAME request instead of the later caller
  // returning immediately (before the context has actually resolved).
  let inflight: Promise<void> | null = null;

  function load(): Promise<void> {
    if (inflight) return inflight;
    inflight = doLoad().finally(() => { inflight = null; });
    return inflight;
  }

  async function doLoad() {
    setState({ loading: true, failed: false });
    try {
      const url = new URL(window.location.href);
      const urlToken = url.searchParams.get("token");

      // Persist a URL token synchronously (before any await) so concurrent first-load API
      // calls already carry the X-Sales-Token header — and take it out of the address bar in the
      // same breath. The token is the rep's credential: left in the URL it goes into the history, into
      // a bookmark, and into whatever the rep pastes to a colleague when the server is having trouble,
      // which is exactly when the resolve below throws. Stored, it is not needed in the URL again.
      if (urlToken) {
        sessionStorage.setItem(SALES_TOKEN_KEY, urlToken);
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      }
      const token = sessionStorage.getItem(SALES_TOKEN_KEY);

      if (token) {
        const res = await api.session.resolveToken(token);
        if (res.status === "ok" && res.context) {
          setState({
            context: res.context, customer: res.customer, hasToken: true, loaded: true,
            devPrincipal: previewFor(res.context),
          });
          return;
        }
        // Invalid/expired token — drop it and fall through to the normal (authenticated) context.
        sessionStorage.removeItem(SALES_TOKEN_KEY);
      }

      const context = await api.session.context();
      // The preview in the same update as the context, so nothing reacts to the one without the other.
      setState({ context, customer: null, hasToken: false, loaded: true, devPrincipal: previewFor(context) });

      // What the server says we are, for http.ts to compare every later response against — a sign-in
      // that lapses mid-session downgrades us to Basic without any call failing.
      setKnownPrincipal(context.principal);
      // A user row means a real sign-in. Anonymous visitors have none, and a signed-in account with no
      // permissions resolves as Basic just like them — so the principal cannot carry this on its own.
      setKnownSignedIn(context.userId != null);
      clearSessionExpired();

      // Fold the signed-in DB user into the session — the single authoritative source for the profile
      // (name/email/company), replacing the per-page api.user.me() calls. Only the authenticated path
      // has a user row (anonymous/Internal have none), so gate on userId. Failure is non-fatal — the
      // profile just falls back to placeholders.
      if (context.userId != null) {
        try {
          const u = await api.user.me();
          setState("user", { name: u.name, email: u.email, dealerId: u.dealerId, companyName: u.companyName });
        } catch (e) {
          console.warn("Failed to load user profile:", e);
        }
      }
    } catch (e) {
      console.warn("Failed to load session context:", e);
      // loaded:true so gated UI settles, but failed:true so consumers know this is NOT a resolved
      // Basic context — HomePage shows a retry banner instead of redirecting a dealer away.
      setState({ loaded: true, failed: true });
    } finally {
      setState("loading", false);
    }
  }

  // The capability set for a synthesized (dev-overridden) principal — mirrors the backend resolver.
  const synthCaps = (p: SessionPrincipal): SessionCapabilities =>
    p === "internal"
      ? { landing: "dashboard", projectScope: "dealer", canSave: true, canOrder: true, orderChannel: "internal", canSeeNetPrice: true }
      : p === "dealer"
        ? { landing: "dashboard", projectScope: "dealer", canSave: true, canOrder: true, orderChannel: "webshop", canSeeNetPrice: true }
        : { landing: "configurator", projectScope: "ownSingle", canSave: true, canOrder: false, orderChannel: "none", canSeeNetPrice: false };

  // Effective principal/capabilities honour a dev override; otherwise the resolved context.
  const principal = (): SessionPrincipal => state.devPrincipal ?? state.context?.principal ?? "basic";
  const caps = (): SessionCapabilities | undefined =>
    state.devPrincipal ? synthCaps(state.devPrincipal) : state.context?.capabilities;

  return {
    state,
    load,
    // Dev-only principal override (gated to admins + DEV builds in the UI).
    setDevPrincipal: (p: SessionPrincipal | null) => {
      setState("devPrincipal", p);
      try {
        if (p) sessionStorage.setItem(DEV_PRINCIPAL_KEY, p);
        else sessionStorage.removeItem(DEV_PRINCIPAL_KEY);
      } catch { /* storage unavailable */ }
    },
    // Sign-out: what this tab holds for the session, which the next sign-in in it must not inherit. The
    // preview, and a rep's deep-link token — the server reads the token before the sign-in, so left
    // behind it would make whoever signs in next an Internal session.
    clearBrowserSession: () => {
      setState("devPrincipal", null);
      sessionRemove(DEV_PRINCIPAL_KEY);
      sessionRemove(SALES_TOKEN_KEY);
    },
    isInternal: () => principal() === "internal",
    isDealer: () => principal() === "dealer",
    isBasic: () => principal() === "basic",
    canSave: () => caps()?.canSave ?? false,
    canOrder: () => caps()?.canOrder ?? false,
    canSeeNetPrice: () => caps()?.canSeeNetPrice ?? false,
    orderChannel: () => caps()?.orderChannel ?? "none",
    // Bare webshop URL (no ?intentId=) for the order popup to sit on while the order resolves.
    // Null/absent ⇒ callers open a blank popup, which is what they did before this existed.
    webShopUrl: () => state.context?.webShopUrl ?? null,
    // Origin of the D365 client that opened this tab (see closeToHost). Null when unconfigured.
    d365Origin: () => state.context?.d365Origin ?? null,
    landing: () => caps()?.landing ?? "configurator",
    // Internal-facing roles (the original App.GarageExpert/GarageInternal/Admin) unlock advanced tooling.
    // In a principal preview (dev switcher), reflect the PREVIEWED actor — only "internal" is expert — so
    // expert-only UI (e.g. the top-bar selector) hides when previewing as basic/dealer, instead of the real
    // admin permissions leaking through. Without an override (normal users), the real permissions decide.
    isExpert: () => {
      if (state.devPrincipal) return state.devPrincipal === "internal";
      const p = state.context?.permissions ?? [];
      return p.includes("admin") || p.includes("garageInternal") || p.includes("garageExpert");
    },
    isAdmin: () => state.context?.permissions?.includes("admin") ?? false,
    // The rep's products — the up-and-over door, garage door partial orders, doors ordered on their own —
    // offered to the Internal principal or the Admin permission only. The server serves the up-and-over door
    // and a door on its own to exactly those readers (UpAndOverEndpoints, ExteriorDoorEndpoints' standalone
    // group, IsAdminScope); a partial order is held to the same readers here, while the server refuses one
    // only when it is ordered (GarageOrderService). Not isExpert: GarageExpert and GarageInternal on a dealer
    // unlock expert options, not these. In a principal preview the previewed actor decides, so previewing as
    // a dealer hides them.
    canUseInternalProducts: () =>
      principal() === "internal" || (!state.devPrincipal && (state.context?.permissions?.includes("admin") ?? false)),
    // May edit the tenant register in the admin area. Marketing holds TenantBranding and nothing else
    // there; Admin implies it. Not affected by the dev principal override — it gates a page, not a flow.
    isTenantEditor: () => {
      const p = state.context?.permissions ?? [];
      return p.includes("admin") || p.includes("tenantBranding");
    },
    // The signed-in DB user (name/email/company) — single source for the profile UI.
    user: () => state.user,
  };
}

const sessionStore = createRoot(createSessionStore);

export const sessionState = sessionStore.state;
export const loadSession = sessionStore.load;
export const setDevPrincipal = sessionStore.setDevPrincipal;
export const clearBrowserSession = sessionStore.clearBrowserSession;
export const isInternal = sessionStore.isInternal;
export const isBasic = sessionStore.isBasic;
export const canSave = sessionStore.canSave;
export const canOrder = sessionStore.canOrder;
export const canSeeNetPrice = sessionStore.canSeeNetPrice;
export const orderChannel = sessionStore.orderChannel;
export const webShopUrl = sessionStore.webShopUrl;
export const d365Origin = sessionStore.d365Origin;
export const isExpert = sessionStore.isExpert;
export const isAdmin = sessionStore.isAdmin;
export const canUseInternalProducts = sessionStore.canUseInternalProducts;
export const isTenantEditor = sessionStore.isTenantEditor;
export const sessionUser = sessionStore.user;
